import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const DEFAULT_VIEW = {
  center: [0, 0],
  zoom: 2,
};

const MAP_STYLES = [
  {
    label: "Default",
    url: "/light-style.json"
  },
  {
    label: "Light",
    url: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
  },
  {
    label: "Dark",
    url: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
  },
  {
    label: "Bright",
    url: "https://tiles.stadiamaps.com/styles/osm_bright.json"
  },
  {
    label: "Demo",
    url: "https://demotiles.maplibre.org/style.json"
  }
];

const MapComponent = forwardRef((props, ref) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [styleUrl, setStyleUrl] = useState(MAP_STYLES[0].url);

  useImperativeHandle(ref, () => ({
    executeCommand: (cmd) => {
      if (!mapRef.current) return;
      const map = mapRef.current;
      const { command, params = {} } = cmd;
      if (command === "move") {
        let dx = 0, dy = 0;
        const value = params.value || cmd.value || 150;
        const direction = params.direction || cmd.direction;
        switch (direction) {
          case "left": dx = -value; break;
          case "right": dx = value; break;
          case "up": dy = -value; break;
          case "down": dy = value; break;
          default: break;
        }
        map.panBy([dx, dy], { duration: 500 });
      } else if (command === "zoom") {
        if (params.action === "in" || cmd.action === "in") map.zoomIn();
        else if (params.action === "out" || cmd.action === "out") map.zoomOut();
        else if (params.level || cmd.level) map.setZoom(params.level || cmd.level);
      } else if (command === "pan_to") {
        const { latitude, longitude, zoom } = { ...cmd, ...params };
        if (latitude && longitude) {
          // Remove old marker if exists
          if (markerRef.current) {
            markerRef.current.remove();
          }
          // Add new marker
          markerRef.current = new maplibregl.Marker()
            .setLngLat([longitude, latitude])
            .addTo(map);

          map.flyTo({
            center: [longitude, latitude],
            zoom: zoom || map.getZoom(),
            essential: true,
          });
        }
      } else if (command === "clear_markers") {
        if (markerRef.current) {
          markerRef.current.remove();
        }
      }
    },
  }));

  useEffect(() => {
    mapRef.current = new maplibregl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: DEFAULT_VIEW.center,
      zoom: DEFAULT_VIEW.zoom,
    });

    // Add navigation controls
    mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right");

    // Geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: 15,
            essential: true,
          });
        },
        () => {
          // Permission denied or error: stay at default view
        }
      );
    }

    return () => {
      mapRef.current && mapRef.current.remove();
    };
    // Only run on mount/unmount
    // eslint-disable-next-line
  }, []);

  // Update map style live
  useEffect(() => {
    if (mapRef.current && styleUrl) {
      mapRef.current.setStyle(styleUrl);
    }
  }, [styleUrl]);

  return (
    <>
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 20, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 6 }}>
        <label htmlFor="map-style-select" style={{ fontWeight: 500, marginRight: 8 }}>Map Style:</label>
        <select
          id="map-style-select"
          value={styleUrl}
          onChange={e => setStyleUrl(e.target.value)}
          style={{ fontSize: '1rem', padding: '2px 8px', borderRadius: 4 }}
        >
          {MAP_STYLES.map(style => (
            <option key={style.url} value={style.url}>{style.label}</option>
          ))}
        </select>
      </div>
      <div ref={mapContainer} className="map-container" />
    </>
  );
});

export default MapComponent; 